import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable, type ColumnDef, type RowData, type SortingState, type Updater,
} from '@tanstack/react-table';
import { cn } from 'cn';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { useMemo, type MouseEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useViewportWidth } from '@/app/viewport';
import { Skeleton } from '@/components/ui/skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** 1 always; 2 hides under 1100 px; 3 hides under 1300 px (docs/ux/shell.md). */
    priority?: 1 | 2 | 3;
    align?: 'left' | 'right';
    width?: number;
    /** Extra classes on head and cell (for example a fixed width). */
    className?: string;
    headLabel?: string;
  }
}

export type QueueTableProps<T> = {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
  getRowId: (row: T) => string;
  rowHref: (row: T) => string;
  rowState?: (row: T) => 'default' | 'overdue';
  caption: string;
  state: 'loading' | 'ready';
  /** Rendered in one full-width cell when there are no rows. */
  empty: ReactNode;
  className?: string;
};

const SKELETON_ROWS = [0, 1, 2, 3, 4, 5];

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, [role="button"], [data-slot="days-cell"]'));
}

/**
 * The dense queue (46 px rows, 13 px Inter, paper surface, head on cream-deep
 * sticky under the top bar). Row click navigates; the trailing Open link is
 * the keyboard target, so rows are not focusable. Column priorities hide
 * columns by viewport width. Below 768 px the screen renders MatterCardList
 * instead, so this component never receives a phone viewport in practice.
 */
export function QueueTable<T>({ columns, data, sorting, onSortingChange, getRowId, rowHref, rowState, caption, state, empty, className }: QueueTableProps<T>) {
  const width = useViewportWidth();
  const navigate = useNavigate();
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    for (const c of columns) {
      const id = c.id ?? ('accessorKey' in c ? String(c.accessorKey) : undefined);
      const p = c.meta?.priority ?? 1;
      if (id) vis[id] = !(p === 3 && width < 1300) && !(p === 2 && width < 1100);
    }
    return vis;
  }, [columns, width]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: true,
  });

  const visibleCount = table.getVisibleLeafColumns().length;

  function onRowClick(e: MouseEvent, row: T) {
    if (isInteractive(e.target)) return;
    if (e.metaKey || e.ctrlKey) {
      window.open(rowHref(row), '_blank', 'noopener');
      return;
    }
    void navigate(rowHref(row));
  }

  return (
    // overflow-clip, not overflow-hidden: hidden would make this box the
    // scroll container the sticky head sticks to, pinning it 52 px down.
    <div data-slot="queue-table" className={cn('overflow-clip rounded-xl border border-beam-line bg-beam-paper', className)}>
      {/* Fixed layout: declared widths hold and the flexible columns share the rest, so
          a wide chip can never push the trailing Open column out of the clipped box. */}
      <table className="w-full table-fixed caption-bottom text-[13px] leading-[1.3]">
        <caption className="sr-only">{caption}</caption>
        <TableHeader className="sticky top-14 z-10 bg-beam-cream-deep md:top-[52px]">
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="border-beam-line hover:bg-beam-cream-deep">
              {hg.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const dir = header.column.getIsSorted();
                const meta = header.column.columnDef.meta;
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    aria-sort={sortable ? (dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none') : undefined}
                    style={meta?.width ? { width: meta.width, minWidth: meta.width } : undefined}
                    className={cn('eyebrow h-9 px-4 text-beam-text-soft', meta?.align === 'right' && 'text-right', meta?.className)}
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn('eyebrow -mx-1 inline-flex h-7 items-center gap-1 rounded px-1 hover:text-foreground', meta?.align === 'right' && 'flex-row-reverse')}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {dir === 'asc' ? <ArrowUpIcon aria-hidden="true" className="size-3" /> : dir === 'desc' ? <ArrowDownIcon aria-hidden="true" className="size-3" /> : null}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {state === 'loading' ? (
            SKELETON_ROWS.map((i) => (
              <TableRow key={i} className="h-[46px] border-beam-line" aria-hidden="true">
                <TableCell colSpan={visibleCount} className="px-4">
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={visibleCount} className="p-4">{empty}</TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const rs = rowState?.(row.original) ?? 'default';
              return (
                <TableRow
                  key={row.id}
                  data-state={rs}
                  onClick={(e) => onRowClick(e, row.original)}
                  className={cn(
                    'h-[46px] cursor-pointer border-beam-line transition-colors duration-[140ms] hover:bg-beam-cream-deep',
                    rs === 'overdue' && 'border-l-[3px] border-l-beam-error bg-beam-blush/30 hover:bg-beam-blush/45',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <TableCell key={cell.id} className={cn('px-4 py-0', meta?.align === 'right' && 'text-right', meta?.className)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </table>
    </div>
  );
}
